import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { EsBaseServiceRequest } from 'src/app/core/models/shared/EsBaseServiceRequest';
import { WorkflowSteps } from 'src/app/shared/base/WorkflowSteps';
import { AnalyticsPageTypeEnum } from 'src/app/shared/analytics/analytics.service';
import { ServiceMetaData } from 'src/app/core/models/shared/ServiceMetaData';

@Injectable()
export class ServiceRequestLoadedStateManager {
  private subject = new Subject<EsBaseServiceRequest>();
  public observable$ = this.subject.asObservable();

  public next(result: EsBaseServiceRequest) {
    this.subject.next(result);
  }
}

@Injectable()
export class ServiceRequestUpdatedStateManager {
  private subject = new Subject<{
    serviceKey: string;
    procedureInstanceId: number;
  }>();
  public observable$ = this.subject.asObservable();

  public next(result: { serviceKey: string; procedureInstanceId: number }) {
    this.subject.next(result);
  }
}

@Injectable()
export class ServiceRequestCreatedStateManager {
  private subject = new Subject<{
    serviceKey: string;
    procedureInstanceId: number;
  }>();
  public observable$ = this.subject.asObservable();

  public next(result: { serviceKey: string; procedureInstanceId: number }) {
    this.subject.next(result);
  }
}

@Injectable()
export class InvalidServiceRequestSubmitStateManager {
  private subject = new Subject<{
    serviceKey: string;
    form: FormGroup;
  }>();
  public observable$ = this.subject.asObservable();

  public next(result: { serviceKey: string; form: FormGroup }) {
    this.subject.next(result);
  }
}

@Injectable()
export class DraftServiceRequestLoadedStateManager {
  private subject = new Subject<{
    procedureInstanceId: number;
    serviceKey: string;
    currentStep: WorkflowSteps;
    result: EsBaseServiceRequest;
  }>();

  public observable$ = this.subject.asObservable();

  public next(event: {
    procedureInstanceId: number;
    serviceKey: string;
    currentStep: WorkflowSteps;
    result: EsBaseServiceRequest;
  }) {
    this.subject.next(event);
  }
}

@Injectable()
export class DraftServiceRequestSavedStateManager {
  private subject = new Subject<{
    procedureInstanceId: number;
    serviceKey: string;
    currentStep: WorkflowSteps;
    result: EsBaseServiceRequest;
    procedureInstanceNumber: string;
  }>();

  public observable$ = this.subject.asObservable();

  public next(event: {
    procedureInstanceId: number;
    serviceKey: string;
    currentStep: WorkflowSteps;
    result: EsBaseServiceRequest;
    procedureInstanceNumber: string;
  }) {
    this.subject.next(event);
  }
}

@Injectable()
export class FormsDefinedStateManager {
  private subject = new Subject<{ serviceKey: string; forms: FormGroup[] }>();

  public observable$ = this.subject.asObservable();

  public next(event: { serviceKey: string; forms: FormGroup[] }) {
    this.subject.next(event);
  }
}

@Injectable()
export class InvalidFlowStateManager {
  private subject = new Subject<{
    serviceKey: string;
    currentStep: WorkflowSteps;
    form: FormGroup;
  }>();

  public observable$ = this.subject.asObservable();

  public next(event: {
    serviceKey: string;
    currentStep: WorkflowSteps;
    form: FormGroup;
  }) {
    this.subject.next(event);
  }
}

@Injectable()
export class SuccessfulFlowStateManager {
  private subject = new Subject<{
    serviceKey: string;
    currentStep: WorkflowSteps;
  }>();

  public observable$ = this.subject.asObservable();

  public next(event: { serviceKey: string; currentStep: WorkflowSteps }) {
    this.subject.next(event);
  }
}

@Injectable()
export class FlowStateManager {
  private subject = new Subject<{
    serviceKey: string;
    currentStep: WorkflowSteps;
    procedureInstanceId: number;
    url: string;
    previousUrl: string;
  }>();

  public observable$ = this.subject.asObservable();

  public next(event: {
    serviceKey: string;
    currentStep: WorkflowSteps;
    procedureInstanceId: number;
    url: string;
    previousUrl: string;
  }) {
    this.subject.next(event);
  }
}

@Injectable()
export class PaymentGatewayStateManager {
  private subject = new Subject<{
    serviceKey: string;
    currentStep: WorkflowSteps;
    procedureInstanceId: number;
    procedureInstanceNumber: string;
  }>();

  public observable$ = this.subject.asObservable();

  public next(event: {
    serviceKey: string;
    currentStep: WorkflowSteps;
    procedureInstanceId: number;
    procedureInstanceNumber: string;
  }) {
    this.subject.next(event);
  }
}

@Injectable()
export class PaymentConfirmedStateManager {
  private subject = new Subject<{
    procedureInstanceId: number;
  }>();

  public observable$ = this.subject.asObservable();

  public next(event: { procedureInstanceId: number }) {
    this.subject.next(event);
  }
}

@Injectable()
export class PaymentErrorStateManager {
  private subject = new Subject<{
    procedureInstanceId: number;
    errors: string;
  }>();

  public observable$ = this.subject.asObservable();

  public next(event: { procedureInstanceId: number; errors: string }) {
    this.subject.next(event);
  }
}

@Injectable()
export class WorkflowStartedStateManager {
  private subject = new Subject<{
    serviceKey: string;
    currentStep: WorkflowSteps;
    registeredUserNumber: string;
    procedureInstanceId: string;
    serviceMetadata: ServiceMetaData;
  }>();

  public observable$ = this.subject.asObservable();

  public next(event: {
    serviceKey: string;
    currentStep: WorkflowSteps;
    registeredUserNumber: string;
    procedureInstanceId: string;
    serviceMetadata: ServiceMetaData;
  }) {
    this.subject.next(event);
  }
}

@Injectable()
export class ServicePageLandedStateManager {
  private subject = new Subject<{
    serviceKey: string;
    currentStep: WorkflowSteps;
    registeredUserNumber: string;
    procedureInstanceId: string;
  }>();

  public observable$ = this.subject.asObservable();

  public next(event: {
    serviceKey: string;
    currentStep: WorkflowSteps;
    registeredUserNumber: string;
    procedureInstanceId: string;
  }) {
    this.subject.next(event);
  }
}
